import { LinkIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { slugify } from '@/lib/utils';
import { createOrgServerFn } from '@/server/functions/organization';

interface CreateOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gitBaseUrl: string;
  onOrgCreated?: () => void;
}

export function CreateOrgDialog({ open, onOpenChange, gitBaseUrl, onOrgCreated }: CreateOrgDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const baseUrlWithoutProtocol = gitBaseUrl.replace(/^https?:\/\//, '');
  const slug = slugify(name);
  const canCreate = name.length >= 2 && slug.length >= 2;

  const handleCreate = async () => {
    if (!canCreate || isCreating) return;

    setIsCreating(true);
    setError(null);

    try {
      const data = await createOrgServerFn({ data: { name, slug } });
      await queryClient.invalidateQueries({ queryKey: ['auth-orgs'] });
      const nextSlug = data.slug ?? slug;
      if (!nextSlug) throw new Error('Failed to resolve organization slug');

      onOrgCreated?.();
      handleClose();
      navigate({ to: '/$orgSlug/tokens', params: { orgSlug: nextSlug } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>Create a new organization to manage your projects and API tokens.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Display Name</Label>
            <Input
              id="org-name"
              placeholder="e.g., My Company, Personal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && canCreate && handleCreate()}
            />
            <p className="text-xs text-mesa-muted">You can change the display name anytime in settings.</p>
          </div>

          {name && (
            <div className="bg-mesa-cream-dark/50 border border-mesa-border p-3 rounded-md">
              <div className="flex items-start gap-2">
                <LinkIcon className="size-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-amber-600 mb-1">Organization URL (permanent)</p>
                  <code className="text-sm break-all">
                    {baseUrlWithoutProtocol}/<span className="font-semibold">{slug || '...'}</span>
                  </code>
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-mesa-red">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canCreate || isCreating}>
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
